import * as utils from 'utils/funcUtils.js';
import { Parser } from 'expr-eval';


export function genInt({ min, max, exclude }) {
    let exclusionsArray = Array.isArray(exclude) ? exclude.map(Number) : [Number(exclude)];
    return utils.getRandomInt(min, max, exclusionsArray);
}

export function genPower({ base, minExp, maxExp }) {
    const exp = utils.getRandomInt(minExp, maxExp);
    return Math.pow(base, exp);
}

export function genIntCommas({ value }) {
    const str = value.toString();

    // Python-style grouping with commas every 3 digits from the right
    return str.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function genIntUnderscores({ value, correct }) {
    // ── prepare the clean pieces ───────────────────────────────────────────
    const raw = String(value).replace(/[,_\s]/g, '');
    const neg = raw.startsWith('-');
    const digits = neg ? raw.slice(1) : raw;
    const sign = neg ? '-' : '';

    // canonical “every-3-digits” layout
    const correctFormat =
        sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, '_');

    // ── easy paths ─────────────────────────────────────────────────────────
    if (correct) return correctFormat;   // the good one
    if (digits.length < 4) return sign + digits;   // <-1000 never uses underscores

    // ── build a few *definitely invalid* patterns ──────────────────────────
    const wrong = new Set([
        '_' + correctFormat,                    // leading _
        correctFormat + '_',                    // trailing _
        sign + digits.slice(0, 2) + '__' +      // double __ inside
        digits.slice(2),
        sign + digits[0] + '_' + digits.slice(1) // underscore after 1st digit
    ]);

    // if the canonical form already contains an underscore,
    // also mangle it by doubling the first one
    if (correctFormat.includes('_'))
        wrong.add(correctFormat.replace('_', '__'));

    // extra bad form for negatives: `-_123…`
    if (neg) wrong.add('-_' + digits);

    // make sure no “technically valid” versions slip in
    wrong.delete(correctFormat);          // the perfect one
    wrong.delete(sign + digits);          // digits-only form

    // (there will always be at least one left)
    const picks = Array.from(wrong);
    return picks[Math.floor(Math.random() * picks.length)];
}


export function genExpression({ expression }) {

    if (expression == null) {
        console.warn('No expression provided for evaluation.');
        return null;
    }

    // ── normalize to string ───────────────────────────
    let expr = typeof expression === 'string' ? expression : String(expression);

    // ── python-to-JS surface pass ─────────────────────
    expr = expr
        .replace(/\bTrue\b/g, 'true')
        .replace(/\bFalse\b/g, 'false')
        .replace(/\bNone\b/g, 'null')
        .replace(/\*\*/g, '^')   // exponent
        .replace(/\blen\s*\(\s*([^)]+?)\s*\)/g, 'length($1)');

    // ── string-method shims ───────────────────────────
    expr = expr
        .replace(/(["'`].*?["'`])\.length\b/g, 'length($1)')
        .replace(/(["'`].*?["'`])\.trim\(\)/g, 'trim($1)')
        .replace(/(["'`].*?["'`])\.toUpperCase\(\)/g, 'upper($1)')
        .replace(/(["'`].*?["'`])\.toLowerCase\(\)/g, 'lower($1)')
        .replace(/(["'`].*?["'`])\.slice\(([^)]+)\)/g, 'slice($1, $2)')
        .replace(/(["'`].*?["'`])\.includes\(([^)]+)\)/g, 'includes($1, $2)')
        // NEW: map "... .repeat(n)" to repeat(str, n)
        .replace(/(["'`].*?["'`])\.repeat\(([^)]+)\)/g, 'repeat($1, $2)');

    // ── parser setup ──────────────────────────────────
    const parser = new Parser();

    const titleCase = (s) =>
        s?.toString().replace(/\b\w/g, (c) => c.toUpperCase()) ?? null;

    parser.functions = {
        ...parser.functions,

        // string helpers
        string: (x) => x?.toString() ?? null,
        length: (x) => x?.length ?? null,
        title: (x) => titleCase(x?.toString()?.trim?.()),
        upper: (x) => x?.toString()?.toUpperCase?.(),
        lower: (x) => x?.toString()?.toLowerCase?.(),
        includes: (x, y) => x?.includes?.(y),
        slice: (x, start, end) => x?.slice?.(start, end),
        trim: (x) => x?.trim?.(),

        // NEW: repeat(str, times)
        repeat: (s, n) => {
            const str = String(s ?? '');
            const times = Number.isFinite(n) ? Math.max(0, n | 0) : 0;
            // optional safety cap to avoid massive allocations:
            if (times > 100000) throw new Error('repeat: times too large');
            return str.repeat(times);
        },

        // math / utility
        clamp: (v, lo, hi) => Math.min(Math.max(v, lo), hi),
        roundTo: (v, p) => Math.round(v * 10 ** p) / 10 ** p,

        // python-style aliases
        len: (x) => x?.length ?? null,
        abs: Math.abs,
        round: (x, n = 0) => Math.round(x * 10 ** n) / 10 ** n,
        floor: Math.floor,
        ceil: Math.ceil,
        pow: Math.pow,
        min: Math.min,
        max: Math.max,
    };

    // ── helper to “Python-ify” the result ─────────────
    const pyify = (val) => {
        if (val === true) return 'True';
        if (val === false) return 'False';
        if (val == null) return 'None';

        if (Array.isArray(val))
            return '[' + val.map(pyify).join(', ') + ']';

        if (typeof val === 'object')
            return '{' +
                Object.entries(val)
                    .map(([k, v]) => `${k}: ${pyify(v)}`)
                    .join(', ') +
                '}';

        return val;
    };

    // ── evaluate ──────────────────────────────────────
    try {
        const result = parser.evaluate(expr);
        return pyify(result);
    } catch (err) {
        console.warn('Invalid expression: ' + err.message);
        return null;
    }
}

export function genOtherOperator({ exclude = [] }) {
    // console.log(exclude)
    const allOperators = ["+", "-", "*", "/"];
    // Filter out all excluded operators
    const available = allOperators.filter(op => !exclude.includes(op));
    if (available.length === 0) throw new Error("No operators left to choose from.");
    // Pick one at random from the remaining options
    const randomIndex = Math.floor(Math.random() * available.length);
    return available[randomIndex];
}



export function genWordInt({ value, separator = "_" }) {
    const ones = [
        "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"
    ];
    const teens = [
        "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen",
        "sixteen", "seventeen", "eighteen", "nineteen"
    ];
    const tens = [
        "", "", "twenty", "thirty", "forty", "fifty",
        "sixty", "seventy", "eighty", "ninety"
    ];

    if (value < 0 || value > 999999) return "";

    function intToWords(n) {
        if (n < 10) return ones[n];
        if (n < 20) return teens[n - 10];
        if (n < 100) {
            const ten = Math.floor(n / 10);
            const rest = n % 10;
            return tens[ten] + (rest ? separator + ones[rest] : "");
        }
        if (n < 1000) {
            const hundred = Math.floor(n / 100);
            const rest = n % 100;
            return ones[hundred] + separator + "hundred" + (rest ? separator + intToWords(rest) : "");
        }
        if (n < 1000000) {
            const thousand = Math.floor(n / 1000);
            const rest = n % 1000;
            return intToWords(thousand) + separator + "thousand" + (rest ? separator + intToWords(rest) : "");
        }
        return "";
    }

    return intToWords(value);
}


export function genWord({ minLength = 0, maxLength = Infinity, exclude = [], excludeKeywords = false } = {}) {
    exclude = Array.isArray(exclude) ? exclude : [exclude];

    const words = [
        "kodeo", "code", "bug", "loop", "array", "pixel",
        "variable", "debug", "compile",
        "data", "output", "logic", "robot", "game", "byte", "script"
    ];

    // Full Python keywords list (built-in and reserved)
    const pythonKeywords = [
        "False", "None", "True", "and", "as", "assert", "async", "await", "break",
        "class", "continue", "def", "del", "elif", "else", "except", "finally", "for",
        "from", "global", "if", "import", "in", "is", "lambda", "nonlocal", "not", "or",
        "pass", "raise", "return", "try", "while", "with", "yield",
        // Common built-ins that are often used as identifiers accidentally
        "compile", "input", "print", "open", "id", "type", "format", "object", "str", "int", "float", "list", "dict", "set", "tuple", "range", "map", "sum", "min", "max", "abs", "round"
    ].map(w => w.toLowerCase());

    const filteredWords = words.filter(word =>
        word.length >= minLength &&
        word.length <= maxLength &&
        !exclude.includes(word) &&
        !(excludeKeywords && pythonKeywords.includes(word.toLowerCase()))
    );


    if (filteredWords.length === 0) {
        throw new Error(`No words found with length between ${minLength} and ${maxLength}`);
    }

    const randomIndex = Math.floor(Math.random() * filteredWords.length);
    return filteredWords[randomIndex];
}


export function genVariable({
    minLength = 0,
    maxLength = Infinity,
    exclude = [],
    type = "int" // can be "int", "float", "string", "list"
} = {}) {
    const variablePools = {
        int: ["x", "y", "i", "n", "b", "a", "c", "count", "num", "value", "size"],
        float: ["x", "y", "i", "n", "b", "a", "c", "num", "value", "temp"],
        string: ["b", "a", "c", "name", "text", "word", "line", "title", "label", "message", "char", "desc"],
        list: ["b", "a", "c", "items", "values", "entries", "results", "records", "data", "elements", "points", "lines"],
        boolean: [
            "is_active", "is_enabled", "is_valid", "is_visible", "is_ready", "is_complete",
            "has_value", "has_data", "has_error", "has_access", "can_run", "can_edit",
            "should_save", "should_update", "should_continue", "should_exit", "should_show",
            "done", "found", "ready", "active", "enabled", "valid", "visible", "success",
            "failed", "exists", "empty", "closed", "selected", "checked", "started",
            "running", "finished", "available", "visible", "on", "off", "flag", "state",
            "ok", "error"
        ]

    };

    const variables = variablePools[type] || variablePools["int"];

    const filteredVariables = variables.filter(v =>
        v.length >= minLength &&
        v.length <= maxLength &&
        !exclude.includes(v)
    );

    if (filteredVariables.length === 0) {
        throw new Error(`No variable names found with length between ${minLength} and ${maxLength} excluding ${exclude.join(", ")} for type "${type}"`);
    }

    const randomIndex = Math.floor(Math.random() * filteredVariables.length);
    return filteredVariables[randomIndex];
}


export function genRepeatString({ str, count }) {
    count = Number(count);
    if (isNaN(count)) {
        throw new Error("Invalid input: count must be a number.");
    }
    str = String(str);
    if (typeof str !== "string" || count < 0) {
        throw new Error("Invalid input: str must be a string and count must be a non-negative number.");
    }
    return str.repeat(count);
}


export function genCountDivisibles({ start, end, divisor }) {

    console.log("Start", typeof start, "End", end, "Divisor", divisor);

    if (start > end) {
        let temp = start;
        start = end;
        end = temp;
    }

    const count = Array.from({ length: end - start + 1 }, (_, i) => i + start)
        .filter(num => num % divisor === 0).length;

    console.log(end - start + 1);
    console.log(`Count of numbers divisible by ${divisor} between ${start} and ${end}: ${count}`);

    return count;
}

export function genBoolean({ opposite } = {}) {
    const normalized = opposite?.toString()?.toLowerCase();
    if (normalized === "true") return "False";
    if (normalized === "false") return "True";
    return Math.random() < 0.5 ? "True" : "False";
}

export function genBoolStringToInt({ boolStr }) {
    if (boolStr == "True") return "1";
    if (boolStr == "False") return "0";
    console.log("Failed Converting boolean string to int:", boolStr);
    return undefined; // Or throw an error if needed
}

export function genComparisonOperator({ exclude = [] } = {}) {
    const ops = ["!=", "==", ">=", "<=", ">", "<"];
    const filtered = ops.filter(op => !exclude.includes(op));
    const idx = Math.floor(Math.random() * filtered.length);
    return filtered[idx];
}



export function genOperatorText({ operator }) {
    const operatorText = {
        "!=": "not equal to",
        "==": "equal to",
        ">=": "greater than or equal to",
        "<=": "less than or equal to",
        ">": "greater than",
        "<": "less than",
        "*": "multiply by",
        "+": "add",
        "-": "subtract",
        "/": "divide by"
    };
    return operatorText[operator] || "";
}


export function genVarFromWord({
    words,
    valid = true,
    exclude = [],
    reservedPrefix = false
}) {
    // Normalize input
    if (typeof words === "string") words = [words];
    if (!Array.isArray(words) || words.length === 0) {
        throw new Error("words must be a non-empty string or array");
    }

    const reserved = [
        "def", "return", "if", "else", "elif", "while", "for", "break", "continue",
        "class", "try", "except", "import", "from", "as", "pass", "in", "is", "not", "and", "or"
    ];

    // For boundaries between words, use realistic mistake characters
    const boundaryChars = ["-", "&", " "];
    // For within a single word, use these forbidden characters
    const forbiddenChars = ["@", "$", "%", "!", "#", "?"];


    function toValidVar(arr) {
        let name = arr
            .join("_")
            .toLowerCase()
            .replace(/[^a-z0-9_]/g, "_")
            .replace(/_{2,}/g, "_")
            .replace(/^_+|_+$/g, "");
        if (/^\d/.test(name)) {
            name = "_" + name;
        }
        if (reserved.includes(name)) {
            name = name + "_var";
        }
        return name;
    }

    // Valid logic: all combos/permutations plus number suffixes
    function toValidVarFromAllCombos(ws) {
        let combos = allWordCombos({ words: ws, permutations: true });
        let options = [];
        combos.forEach(arr => {
            let base = toValidVar(arr);
            options.push(base);
            // Also add with random number at end, if not already ends with digits
            if (!/\d+$/.test(base)) {
                const randomNum = Math.floor(Math.random() * 999) + 1; // 1–999
                options.push(base + randomNum);
            }
        });
        // Remove duplicates and excludes
        const filtered = options.filter((opt, idx) =>
            options.indexOf(opt) === idx && !exclude.includes(opt)
        );
        if (filtered.length === 0) {
            throw new Error("No unique valid variable names could be generated; check your exclude list.");
        }
        return filtered[Math.floor(Math.random() * filtered.length)];
    }

    function allForbiddenCharSingle(word) {
        const forbiddenChars = ["@", "$", "%", "!", "#", "?"];
        const vowels = ["a", "e", "i", "o", "u", "A", "E", "I", "O", "U"];
        let results = [];

        // Find all vowel positions
        const vowelIndices = Array.from(word)
            .map((c, i) => vowels.includes(c) ? i : -1)
            .filter(i => i >= 0);

        // For each vowel, for each forbidden char, replace
        vowelIndices.forEach(idx => {
            forbiddenChars.forEach(ch => {
                results.push(word.slice(0, idx) + ch + word.slice(idx + 1));
            });
        });

        // Also, add start/end forbidden char variants
        forbiddenChars.forEach(ch => {
            results.push(ch + word);
            results.push(word + ch);
        });

        // Deduplicate
        return [...new Set(results)];
    }


    function allBoundaryVariants(ws) {
        let results = [];
        for (let boundaryIdx = 1; boundaryIdx < ws.length; boundaryIdx++) {
            boundaryChars.forEach(ch => {
                results.push(
                    ws.slice(0, boundaryIdx).join("_") +
                    ch +
                    ws.slice(boundaryIdx).join("_")
                );
            });
        }
        return results;
    }

    function toInvalidVar(ws) {
        let combos = allWordCombos({ words: ws, permutations: true });
        const digit = Math.floor(Math.random() * 9) + 1;
        let options = [];

        combos.forEach(wordsArr => {
            if (wordsArr.length === 1) {
                const w = wordsArr[0];
                // Add ALL possible forbidden char variants for every vowel
                options.push(...allForbiddenCharSingle(w));
                if (reserved.includes(w.toLowerCase()) || /^\d+$/.test(w)) {
                    options.push(w);
                }
            } else {
                options.push(...allBoundaryVariants(wordsArr));
                options.push(digit + wordsArr.join("_"));
                if (reservedPrefix) {
                    options.push(reserved[0] + "_" + wordsArr.join("_"));
                }
            }
        });

        combos.forEach(wordsArr => {
            if (wordsArr.every(w => /^\d+$/.test(w))) {
                options.push(wordsArr.join(""));
            }
        });

        const filtered = options.filter((opt, idx) =>
            options.indexOf(opt) === idx && !exclude.includes(opt)
        );
        if (filtered.length === 0) {
            throw new Error("No unique invalid variable names could be generated; check your exclude list.");
        }
        return filtered[Math.floor(Math.random() * filtered.length)];
    }



    let varName, tries = 0;
    do {
        varName = valid ? toValidVarFromAllCombos(words) : toInvalidVar(words);
        tries++;
    } while (exclude.includes(varName) && tries < 10);

    if (exclude.includes(varName)) {
        throw new Error("Could not generate a unique variable not in exclude after 10 tries.");
    }

    return varName;
}

export function genIncorrectIfHeader({ expression }) {
    const patterns = [
        // Capitalization mistakes
        expr => `If ${expr}:`,
        expr => `If ${expr}`,
        // Missing colon
        expr => `if ${expr}`,
        // Extra/missing colons or semicolon
        expr => `if ${expr}::`,
        expr => `if:${expr}`,
        expr => `if :${expr}`,
        // expr => `if ${expr} :`, // -- ISNT this valid??
        expr => `if ${expr};`,
        // No 'if' at all
        expr => `${expr}:`,
        // No expression
        () => `if :`,
        // Colon in wrong place
        expr => `if:${expr}:`,
        expr => `if :${expr}:`,
        // Colon before expression
        expr => `if: ${expr}`,
        // Both capital and missing colon
        expr => `If ${expr}`
    ];
    // Pick one at random
    return patterns[Math.floor(Math.random() * patterns.length)](expression);
}

export function genComparisonOperatorForOutcome({ left, right, outcome }) {
    const maxAttempts = 100;
    let attempt = 0;
    let operator, exprResult;

    // Convert outcome string to boolean
    const desired = outcome === "True";

    function evalExpression(l, op, r) {
        switch (op) {
            case '==': return l == r;
            case '!=': return l != r;
            case '>': return l > r;
            case '<': return l < r;
            case '>=': return l >= r;
            case '<=': return l <= r;
            default: throw new Error(`Unknown operator: ${op}`);
        }
    }

    while (attempt < maxAttempts) {
        operator = genComparisonOperator();
        exprResult = evalExpression(left, operator, right);
        if (exprResult === desired) {
            return operator;
        }
        attempt++;
    }
    throw new Error('Could not generate a comparison operator that matches the desired outcome within reasonable attempts.');
}

export function allWordCombos({ words = [], permutations = true } = {}) {
    if (!Array.isArray(words)) words = [];

    function powerSet(arr) {
        const result = [];
        const n = arr.length;
        for (let i = 1; i < (1 << n); i++) {
            const subset = [];
            for (let j = 0; j < n; j++) {
                if (i & (1 << j)) subset.push(arr[j]);
            }
            result.push(subset);
        }
        return result;
    }

    function* permute(arr, n = arr.length) {
        if (n <= 1) yield arr.slice();
        else {
            for (let i = 0; i < n; i++) {
                yield* permute(arr, n - 1);
                const j = n % 2 ? 0 : i;
                [arr[n - 1], arr[j]] = [arr[j], arr[n - 1]];
            }
        }
    }

    const combos = [];
    const sets = powerSet(words);
    sets.forEach(set => {
        if (permutations) {
            for (let p of permute(set.slice())) combos.push(p);
        } else {
            combos.push(set.slice());
        }
    });

    const seen = new Set();
    return combos.filter(combo => {
        const key = combo.join('\0');
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

export function genWordCombo({ words = [], exclude = [], permutations = true } = {}) {
    const combos = allWordCombos({ words, permutations }).map(c => c.join('\n'));
    const filtered = combos.filter(c => !exclude.includes(c));
    if (filtered.length === 0) {
        throw new Error('No combos available after applying exclusions.');
    }
    return filtered[Math.floor(Math.random() * filtered.length)];
}

export function genIntForUniqueOutcome({
    start,
    operator,
    min,
    max,
    excludeResults = [],
    maxAttempts = 1000,
    exclude0 = true
}) {
    const starts = Array.isArray(start) ? start.map(Number) : [Number(start)];

    let exclusions = Array.isArray(excludeResults)
        ? excludeResults.map(Number)
        : [Number(excludeResults)];

    if (exclude0 && !exclusions.includes(0)) {
        exclusions = [0, ...exclusions];
    }

    if (operator === '*' && !exclusions.includes(1)) {
        exclusions = [1, ...exclusions];
    }

    const apply = (a, op, b) => {
        switch (op) {
            case '+': return a + b;
            case '-': return a - b;
            case '*': return a * b;
            case '/': return a / b;
            default: throw new Error(`Unsupported operator: ${op}`);
        }
    };

    for (let i = 0; i < maxAttempts; i++) {
        const operand = utils.getRandomInt(min, max);

        if (operand === 0) continue;

        const anyExcluded = starts.some(s => exclusions.includes(apply(s, operator, operand)));
        if (!anyExcluded) {
            return operand;
        }
    }

    throw new Error(`No unique operand found within maxAttempts for ${start}. Searched for numbers between ${min} and ${max} with operator "${operator}" and exclusions: [${exclusions.join(', ')}]`);
}

export function genBabyName({
    minLength = 0,
    maxLength = Infinity,
    exclude = []
} = {}) {
    exclude = Array.isArray(exclude) ? exclude : [exclude];

    const babyNames = [
        // Girls
        "Olivia", "Emma", "Ava", "Sophie", "Bella", "Mia", "Amy", "Emily", "Ella",
        "Grace", "Chloe", "Hannah", "Abby", "Katie", "Sarah", "Megan", "Lucy", "Zoe", "Lily",
        // Boys
        "Liam", "Noah", "Will", "James", "Ben", "Luke", "Henry", "Alex", "Mike", "Dan",
        "Ethan", "Matt", "Jack", "Logan", "Jake", "Sam", "Dave", "Joe", "Andy", "Chris"
    ];

    const filteredNames = babyNames.filter(name =>
        name.length >= minLength &&
        name.length <= maxLength &&
        !exclude.includes(name)
    );

    if (filteredNames.length === 0) {
        throw new Error(`No baby names found with length between ${minLength} and ${maxLength}`);
    }

    const randomIndex = Math.floor(Math.random() * filteredNames.length);
    return filteredNames[randomIndex];
}
